import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    Card,
    Container,
    Grid,
    IconButton,
    InputAdornment,
    Link as MuiLink,
    TextField,
    Typography
} from '@mui/material';
import {Link, useNavigate} from "react-router-dom";
import {useAuth} from "../auth-context";
import axios from "axios";
import {signInWithCustomToken} from 'firebase/auth'
import {auth} from "../core/firebase";
import Logo from "../core/components/Logo";
import {ThemedText} from "../core/components/ThemedText";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {isPasskeySupported, loginWithPasskey} from '../passkey/PasskeyService';


function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [passkeyError, setPasskeyError] = useState('');
    const [passkeySupported, setPasskeySupported] = useState(false);
    const [passkeyLoading, setPasskeyLoading] = useState(false);
    const navigate = useNavigate();
    const { login } = useAuth(); // Get the login function from context

    useEffect(() => {
        let isMounted = true;
        const checkSupport = async () => {
            const supported = await isPasskeySupported();
            if (isMounted) setPasskeySupported(supported);
        };
        checkSupport();
        return () => {
            isMounted = false;
        };
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();

        try {
            setErrorMessage('');
            const client = axios.create({
                baseURL: process.env.REACT_APP_API_URL,
            });

            const { data, status } = await client.post('/auth/login', {
                email,
                password
            });

            if (!status === 200) {
                throw new Error('Network response was not ok');
            }
            await login(data);
            const user = await signInWithCustomToken(auth, data.firebase_token);
            console.log(user);
            navigate('/')
        } catch (error) {
            setErrorMessage('Login failed. Please check your credentials and try again.');
        }
    };

    const handlePasskeyLogin = async () => {
        setPasskeyError('');
        setErrorMessage('');
        setPasskeyLoading(true);
        try {
            const client = axios.create({
                baseURL: process.env.REACT_APP_API_URL,
            });
            const identifier = email?.trim() ? email.trim() : undefined;
            const { data, status } = await loginWithPasskey(client, identifier);

            if (!status === 200) {
                throw new Error('Network response was not ok');
            }

            await login(data);
            await signInWithCustomToken(auth, data.firebase_token);
            navigate('/');
        } catch (error) {
            const isCancelled = error?.name === 'AbortError' || error?.name === 'NotAllowedError';
            setPasskeyError(
                isCancelled
                    ? 'Passkey sign-in was cancelled.'
                    : 'Passkey login failed. Please try your password instead.'
            );
        } finally {
            setPasskeyLoading(false);
        }
    };

    return (
        <Container maxWidth="xs">
            <Box
                sx={{
                    marginTop: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Card sx={{ p: 3, width: '100%' }}>
                    <Logo />
                    <ThemedText style={{ marginTop: '20px', marginBottom: '20px'}} align={"center"} type={'title'}>
                        Sign In
                    </ThemedText>
                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                        {errorMessage && (
                            <Typography variant="body2" color="error" align="center" sx={{ mb: 2 }}>
                                {errorMessage}
                            </Typography>
                        )}
                        {passkeyError && (
                            <Typography variant="body2" color="error" align="center" sx={{ mb: 2 }}>
                                {passkeyError}
                            </Typography>
                        )}
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '15px'}}>
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2 }}
                            >
                                Sign In
                            </Button>
                        </Box>
                        {passkeySupported && (
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Button
                                    type="button"
                                    fullWidth
                                    variant="outlined"
                                    onClick={handlePasskeyLogin}
                                    disabled={passkeyLoading}
                                    sx={{ mb: 2 }}
                                >
                                    {passkeyLoading ? 'Waiting for passkey...' : 'Continue with Passkey'}
                                </Button>
                            </Box>
                        )}
                        <Grid container>
                            <Grid item xs>
                                <Link to="/forgot-password" style={{ textDecoration: 'none'}}>
                                    <MuiLink variant="body2" style={{ textDecoration: 'none'}}>
                                        Forgot password?
                                    </MuiLink>
                                </Link>
                            </Grid>
                            <Grid item>
                                <Link to="/register" style={{ textDecoration: 'none'}}>
                                    <MuiLink variant="body2" style={{ textDecoration: 'none'}}>
                                        {"Don't have an account? Register"}
                                    </MuiLink>
                                </Link>
                            </Grid>
                        </Grid>
                    </Box>
                </Card>
            </Box>
        </Container>
    );
}

export default Login;
